import useCurrentUser from "@/hooks/useCurrentUser";
import { useStores } from "@/hooks/useStores";
import apiClient from "@/lib/apiClient";
import { NoteType } from "@/src/notes/types/NoteType";
import { useCallback } from "react";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import useSWRMutation from "swr/mutation";
import { z } from "zod";

const PAGE_SIZE = 10;

const UseNotesSchema = z.object({
  elementId: z.coerce.number(),
  initialNotes: z.array(z.custom<NoteType>()).optional(),
});

type UseNotesOptions = z.infer<typeof UseNotesSchema>;

// This is the main hook that does practically everything related to notes on the client side.
const useNotes = (options: UseNotesOptions) => {
  const { elementId, initialNotes } = UseNotesSchema.parse(options);

  // We use the auth headers to pass the jwt to the server
  const { authStore } = useStores();
  const { getAuthHeaders } = authStore;

  const { currentUser } = useCurrentUser();

  const userUid = currentUser?.uid;

  // This is the key function that gets the notes from the server
  const getKey = useCallback(
    (pageIndex: number, previousPageData) => {
      if (previousPageData && !previousPageData.notes?.length) return null;
      const offset = pageIndex * PAGE_SIZE;
      return `/notes/${elementId}?offset=${offset}&limit=${PAGE_SIZE}`;
    },
    [elementId]
  );

  // This is the fetcher function that gets the notes from the api
  const fetcher = useCallback(
    async (url: string) => {
      const response = await apiClient.get(url, currentUser?.uid);
      return response;
    },
    [currentUser?.uid]
  );

  // We use swrInfinite to get the notes from the server, which nicely handles pagination for us.
  // The fallback data is used to pass the initial notes to the hook from getStaticProps
  const {
    data: pages,
    error,
    size,
    setSize,
    mutate,
    isLoading,
  } = useSWRInfinite(getKey, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    fallbackData: initialNotes ? [{ notes: initialNotes }] : undefined,
  });

  // Flatten the pages of notes into a single array
  const notes = pages
    ? pages.flatMap((page) => page?.notes)
    : initialNotes || [];

  // Calculate the total number of notes and pages
  const totalNotes = pages?.[0]?.metadata?.total || initialNotes?.length || 0;

  // Calculate the total number of notes including replies
  const totalNotesIncludingReplies =
    pages?.[0]?.metadata?.totalIncludingReplies || initialNotes?.length || 0;

  // Calculate the total number of pages
  const totalPages =
    pages?.[0]?.metadata?.totalPages ||
    (initialNotes ? Math.ceil(initialNotes.length / PAGE_SIZE) : 0);

  // Determine whether we are loading more notes
  const isLoadingMore =
    isLoading || (size > 0 && pages && typeof pages[size - 1] === "undefined");

  // We might not have any notes at all
  const isEmpty = pages?.[0]?.notes?.length === 0;

  // Determine whether we have reached the end.
  // This is calculated by checking if we have less than PAGE_SIZE notes in the last page.
  const isReachingEnd =
    isEmpty || (pages && pages[pages.length - 1]?.notes?.length < PAGE_SIZE);

  // This is the mutation function that creates a new note
  // SWR mutations are useful for creating, updating, deleting, etc. because it uses the same api as the request version of useSWR
  // It offers other features we aren't using to the fullest, yet.
  const { trigger: createNote, isMutating: isCreating } = useSWRMutation(
    "notes/create",
    async (
      key,
      {
        arg,
      }: {
        arg: {
          uid: string;
          content: string;
          imageUrls: string[];
          parentUid?: string;
        };
      }
    ) => {
      const payload = { ...arg };

      // If the parentUid is null, we need to remove it from the payload
      if (payload.parentUid === null) {
        delete payload.parentUid;
      }
      return apiClient.post(`/notes/${elementId}`, userUid, payload);
    },
    {
      onSuccess: (newNoteData) => {
        // Calling mutate like this just re-requests the data from the server (including the new note).
        // We could include the existing data plus the new note, but merging the new note is relative complicated in a paginated data structure.
        mutate();
      },
    }
  );

  // User likes
  const userLikesFetcher = useCallback(
    async (url: string) => {
      if (!userUid) return [];
      const headers = await getAuthHeaders();
      const response = await apiClient.get(url, { headers });
      return response.likes || [];
    },
    [getAuthHeaders, userUid]
  );

  // Use this to get he current user's likes for the currently viewed element.
  // This is what's used to highlight the heart icon in the UI.
  const { data: userLikes, mutate: mutateUserLikes } = useSWR(
    userUid ? `/notes/${elementId}/likes/user` : null,
    userLikesFetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // This is the mutation function that creates a new like
  const { trigger: likeNote, isMutating: isLiking } = useSWRMutation(
    "notes/like",
    async (key, { arg: noteUid }: { arg: string }) => {
      // Immediately update the user's likes
      mutateUserLikes(
        (currentData) => {
          return [...currentData, noteUid];
        },
        {
          revalidate: false,
        }
      );

      // Loop through the pages of notes, find the note or reply, and immediately update the likesCount
      // We set the data immediately because it looks better in the UI, and it'd be a bit wasteful to request all notes again.
      mutate(
        (currentPages) => {
          if (!currentPages) return currentPages;

          return currentPages.map((page) => {
            if (!page || !page.notes) return page;

            return {
              ...page,
              notes: page.notes.map((note) => {
                if (note.uid === noteUid) {
                  return {
                    ...note,
                    likesCount: (note.likesCount || 0) + 1,
                  };
                }
                if (note.replies) {
                  return {
                    ...note,
                    replies: note.replies.map((reply) => {
                      if (reply.uid === noteUid) {
                        return {
                          ...reply,
                          likesCount: (reply.likesCount || 0) + 1,
                        };
                      }
                      return reply;
                    }),
                  };
                }
                return note;
              }),
            };
          });
        },
        {
          revalidate: false,
        }
      );

      const result = await apiClient.post(
        `/notes/${elementId}/${noteUid}/likes`,
        userUid,
        {}
      );

      return result;
    }
  );

  // We do the opposite of likeNote
  const { trigger: unlikeNote, isMutating: isUnliking } = useSWRMutation(
    "notes/unlike",
    async (key, { arg: noteUid }: { arg: string }) => {
      // Immediately update the user's likes
      mutateUserLikes(
        (currentLikes) => {
          return currentLikes?.filter((uid) => uid !== noteUid);
        },
        {
          revalidate: false,
        }
      );

      // Loop through the pages of notes, find the note or reply, and immediately update the likesCount
      mutate(
        (currentPages) => {
          if (!currentPages) return currentPages;

          return currentPages.map((page) => {
            if (!page || !page.notes) return page;

            return {
              ...page,
              notes: page.notes.map((note) => {
                if (note.uid === noteUid) {
                  return {
                    ...note,
                    likesCount: Math.max((note.likesCount || 0) - 1, 0),
                  };
                }
                if (note.replies) {
                  return {
                    ...note,
                    replies: note.replies.map((reply) => {
                      if (reply.uid === noteUid) {
                        return {
                          ...reply,
                          likesCount: Math.max((reply.likesCount || 0) - 1, 0),
                        };
                      }
                      return reply;
                    }),
                  };
                }
                return note;
              }),
            };
          });
        },
        {
          revalidate: false,
        }
      );

      const result = await apiClient.delete(
        `/notes/${elementId}/${noteUid}/likes`,
        userUid
      );

      return result;
    },
    {
      onSuccess: (data, key, config) => {},
    }
  );

  // This is the mutation function that updates a note
  const { trigger: updateNote, isMutating: isUpdating } = useSWRMutation(
    "notes/update",
    async (
      key,
      { arg }: { arg: { uid: string; content: string; imageUrls: string[] } }
    ) => {
      return apiClient.patch(`/notes/${elementId}/${arg.uid}`, userUid, arg);
    },
    {
      onSuccess: (updatedNoteData) => {
        // We could mutate the individual note, but this is simpler, frankly.
        mutate();
      },
    }
  );

  // This is the mutation function that deletes a note
  const { trigger: deleteNote, isMutating: isDeleting } = useSWRMutation(
    "notes/delete",
    async (key, { arg: noteUid }: { arg: string }) => {
      const result = await apiClient.delete(
        `/notes/${elementId}/${noteUid}`,
        userUid
      );

      // It's easier to just mutate the whole thing, rather than find the note in a paginated data structure to remove
      mutate();

      return result;
    }
  );

  // Currently not used
  const { trigger: disableNote, isMutating: isDisabling } = useSWRMutation(
    "notes/disable",
    async (key, { arg: noteUid }: { arg: string }) => {
      const result = await apiClient.patch(
        `/notes/${elementId}/${noteUid}/disable`,
        userUid,
        {}
      );

      mutate();

      return result;
    }
  );

  const { trigger: reportNote, isMutating: isReporting } = useSWRMutation(
    "notes/report",
    async (key, { arg: noteUid }: { arg: string }) => {
      const result = await apiClient.patch(
        `/notes/${elementId}/${noteUid}/report`,
        userUid,
        {}
      );

      // Optionally, you can update the local state here if needed
      // For example, you might want to add a 'reported' flag to the note

      return result;
    }
  );

  // We currently don't use this, we unreport notes in the admin section.
  const { trigger: unreportNote, isMutating: isUnreporting } = useSWRMutation(
    "notes/unreport",
    async (key, { arg: noteUid }: { arg: string }) => {
      const result = await apiClient.patch(
        `/notes/${elementId}/${noteUid}/unreport`,
        userUid,
        {}
      );

      return result;
    }
  );

  // Pin and unpin notes are used to pin notes to the top of the list
  const { trigger: pinNote, isMutating: isPinning } = useSWRMutation(
    "notes/pin",
    async (key, { arg: noteUid }: { arg: string }) => {
      const result = await apiClient.patch(
        `/notes/${elementId}/${noteUid}/pin`,
        userUid,
        {}
      );

      mutate(
        (currentPages) => {
          if (!currentPages) return currentPages;

          return currentPages.map((page) => ({
            ...page,
            notes: page.notes.map((note) =>
              note.uid === noteUid ? { ...note, isPinned: true } : note
            ),
          }));
        },
        {
          revalidate: false,
        }
      );

      return result;
    }
  );

  const { trigger: unpinNote, isMutating: isUnpinning } = useSWRMutation(
    "notes/unpin",
    async (key, { arg: noteUid }: { arg: string }) => {
      const result = await apiClient.patch(
        `/notes/${elementId}/${noteUid}/unpin`,
        userUid,
        {}
      );

      mutate(
        (currentPages) => {
          if (!currentPages) return currentPages;

          return currentPages.map((page) => ({
            ...page,
            notes: page.notes.map((note) =>
              note.uid === noteUid ? { ...note, isPinned: false } : note
            ),
          }));
        },
        {
          revalidate: false,
        }
      );

      return result;
    }
  );

  return {
    notes,
    size,
    error,
    isLoading,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    loadMore: () => {
      setSize(size + 1);
    },
    createNote,
    likeNote,
    unlikeNote,
    updateNote,
    deleteNote,
    disableNote,
    reportNote,
    unreportNote,
    isCreating,
    isLiking,
    isUnliking,
    isUpdating,
    isDeleting,
    isDisabling,
    isReporting,
    isUnreporting,
    totalNotes,
    totalNotesIncludingReplies,
    totalPages,
    userLikes,
    mutateUserLikes,
    initialNotesUsed: !!initialNotes,
    pinNote,
    unpinNote,
    isPinning,
    isUnpinning,
  };
};

export default useNotes;
